import React from 'react';
import { isChunkLoadError } from '../utils/lazyWithRetry';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Carry the error into the first error render so the fallback can tell a
    // stale-asset failure from an application bug; componentDidCatch adds the
    // component stack afterwards.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    if (this.props.onRetry) {
      this.props.onRetry();
    }
    
    // Force a re-render by updating the key
    this.forceUpdate();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;

      // A chunk that never arrived is not an application error, and retrying
      // in place cannot fix it: React.lazy caches the rejected import for the
      // life of the document. lazyWithRetry has already retried and reloaded
      // once by this point, so all that is left to offer is a manual reload.
      // Takes precedence over any custom fallback — none of them describe this.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="error-boundary-container">
            <div className="error-boundary-content">
              <div className="error-boundary-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <h2 className="error-boundary-title">Couldn&apos;t load this page</h2>

              <p className="error-boundary-message">
                Part of the app failed to download. This usually means MemBrowse was
                updated while your tab was open, or the connection dropped. Reloading
                fixes it.
              </p>

              <div className="error-boundary-actions">
                <button
                  onClick={() => window.location.reload()}
                  className="error-boundary-refresh-button"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        );
      }

      if (Fallback) {
        return (
          <Fallback 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onReset={this.handleRetry}
          />
        );
      }

      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <h2 className="error-boundary-title">Something went wrong</h2>
            
            <p className="error-boundary-message">
              An unexpected error occurred while rendering this component. Please try refreshing the page.
            </p>
            
            <div className="error-boundary-actions">
              <button 
                onClick={() => window.location.reload()}
                className="error-boundary-refresh-button"
              >
                Refresh Page
              </button>
              
              <button 
                onClick={this.handleRetry}
                className="error-boundary-retry-button"
              >
                Try Again
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-boundary-stack">
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <strong>Stack:</strong>
                  {/* errorInfo only arrives in componentDidCatch, one render
                      after the error itself. */}
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;